// ================= THE EXPANDED CARD =================
//
// ONE RENDERER FOR A CARD READ IN FULL, out of js/ui.js for the same reason the compact tile
// left it (js/card-tile.js, whose header tells the whole story): a surface that is not the game
// shell needed the renderer, and the alternative -- a second renderer -- drifts, silently,
// because no assertion anywhere makes two renderers agree. The tile's move drew its boundary at
// "helpers that only OTHER surfaces use (ownershipTagsHtml, familyMotifHtml, staminaPips,
// statFigureHtml, guardPipsHtml, the expanded panels) stayed behind" -- right until library.html
// grew a card sheet, rendered the compact tile into it because that was what it could reach, and
// became the one place in the game where a card's Conditions could not be read properly. That is
// the drift the tile's header predicted, arrived on schedule. So the boundary moved.
//
// WHAT LIVES HERE: the expanded drawer (railDetailHtml -- Zone 3's card inspector, the Telegraph
// sheet, the Setup deck preview, and now the library's card sheet all draw it), the two role
// panels Commit composes directly (attackPanelHtml / defensePanelHtml), and their transitive
// closure: the banded silhouette wrapper, the Conditions disclosure, the coverage sentence, the
// stat figures, the consequence line, the range-fit caveat, the v4 tokens, the ownership tags,
// the family motion field. Pure motion -- ui.js re-binds every name and its call sites read
// exactly as they did.
//
// THE HOST SEAM. Three reads reached into ui.js's bout state and could not travel: which range
// the fight is standing at (the badge rows' "you are here" marker), where the Conditions
// disclosure remembers its open state (SESSION, so it survives Commit's re-renders), and which
// bands the opponent's revealed pair cannot cover (the aiming figure's gap markers). Each is a
// HOST hook now: ui.js binds the bout-aware versions at load, and the defaults answer "there is
// no bout" -- no marker, page-local disclosure state, no gap hints -- which is exactly what a
// card rendered on the collection page should say. The onclicks a bout needs (silhouettePick,
// setCommitStrikeLevel, setRailInspect) still say `UI.` -- they are only ever emitted when a
// commit draft or a rail index is passed, and no page without ui.js passes one.
//
// WHAT DID NOT MOVE: opponentUncoveredBands (it reads the opponent's selection out of SESSION --
// it is a fact about the bout, not about a card), the conditional shelf (Commit-only controls),
// and the Commit stage itself. The disclosure's DOM toggle moved; its per-bout memory stayed
// behind the seam.
//
// Loads after cards.js, card-tile.js, ui-silhouette.js and mechanics-dictionary.js; before
// ui.js, which re-binds. Everything else -- window.CARDS, window.ENGINE, window.UISheet,
// window.MoveArt -- is read at CALL time, so this file has no opinion about where those sit.
(function () {
  'use strict';

  // The compact tile's helpers, re-bound by name exactly as ui.js re-binds them -- the drawer
  // prints the same meter, gloss, badges and glyphs the tile does, from the same functions.
  const CT = window.CardTile;
  const escapeHtml = CT.escapeHtml;
  const ownershipColor = CT.ownershipColor;
  const ownershipLabel = CT.ownershipLabel;
  const styleSigilHtml = CT.styleSigilHtml;
  const moveGlossHtml = CT.moveGlossHtml;
  const rangeBadgeRow = CT.rangeBadgeRow;
  const speedMeterHtml = CT.speedMeterHtml;
  const conditionsText = CT.conditionsText;
  const POWER_BURST = CT.POWER_BURST;
  const GUARD_SHIELD = CT.GUARD_SHIELD;
  const FAMILY_COLORS = window.FAMILY_COLORS;

  // ---------------- The host seam ----------------
  // Disclosure state for pages with no bout: lives for the page, which is what "stored state
  // still wins once it exists" can mean when there is no SESSION to outlive a re-render.
  const PAGE_RULES_OPEN = {};
  let HOST = {
    boutRange: function () { return null; },
    storedRulesOpen: function (key) { return PAGE_RULES_OPEN[key]; },
    storeRulesOpen: function (key, next) { PAGE_RULES_OPEN[key] = next; return true; },
    uncoveredBands: function () { return []; }
  };
  function bindHost(h) { HOST = Object.assign({}, HOST, h); }

  // ---------------- Ownership / role visual language ----------------
  // ownershipColor and ownershipLabel are card-tile.js's, reached through the delegating consts
  // above; the ownership TAGS and the family motion field are expanded-card dress, so they moved
  // here with the drawer. FAMILY_COLORS itself still lives in js/cards.js -- the fighter HUD
  // and Zone 3's beat rows read the same palette, and three copies of a colour table is two too
  // many.
  function ownershipTagsHtml(card) {
    let html = `<span class="ribbon-tag" style="--frame:${ownershipColor(card)}">${escapeHtml(ownershipLabel(card))}</span>`;
    if (card.dirty) html += `<span class="ribbon-tag dirty">Dirty</span>`;
    return html;
  }

  // ---------------- Visual language: family motion field ----------------
  // (The Style sigils and styleSigilHtml went to js/card-tile.js -- the compact tile prints one,
  // and the drawer title rows below reach it through the delegating const above.)
  //
  // Low-opacity background traces suggesting each family's shared biomechanical engine (see the
  // design doc's Family Geometry page) — shown only on the expanded card, never on compact planks,
  // and never allowed to compete with the Attack/Defense text (see .motion-field CSS: 6-14% opacity
  // at rest, ~22% on hover/selection/resolution).
  const FAMILY_MOTIFS = {
    KineticImpact: '<svg viewBox="0 0 240 140" preserveAspectRatio="none"><line x1="10" y1="20" x2="205" y2="20" stroke="{c}" stroke-width="2"/><line x1="30" y1="42" x2="205" y2="42" stroke="{c}" stroke-width="2"/><line x1="55" y1="64" x2="205" y2="64" stroke="{c}" stroke-width="2"/><line x1="30" y1="86" x2="205" y2="86" stroke="{c}" stroke-width="2"/><line x1="10" y1="108" x2="205" y2="108" stroke="{c}" stroke-width="2"/><line x1="205" y1="8" x2="205" y2="120" stroke="{c}" stroke-width="3"/></svg>',
    TorquePressure: '<svg viewBox="0 0 240 140" preserveAspectRatio="none"><circle cx="185" cy="70" r="18" stroke="{c}" stroke-width="2" fill="none"/><circle cx="185" cy="70" r="38" stroke="{c}" stroke-width="2" fill="none"/><circle cx="185" cy="70" r="58" stroke="{c}" stroke-width="2" fill="none"/><circle cx="185" cy="70" r="78" stroke="{c}" stroke-width="2" fill="none"/></svg>',
    FulcrumFlow: '<svg viewBox="0 0 240 140" preserveAspectRatio="none"><path d="M10 20 C 100 20, 140 60, 230 40" stroke="{c}" stroke-width="2" fill="none"/><path d="M10 70 C 100 70, 140 30, 230 90" stroke="{c}" stroke-width="2" fill="none"/><path d="M10 120 C 100 120, 140 100, 230 110" stroke="{c}" stroke-width="2" fill="none"/></svg>',
    LeverageBase: '<svg viewBox="0 0 240 140" preserveAspectRatio="none"><line x1="10" y1="120" x2="230" y2="120" stroke="{c}" stroke-width="2"/><line x1="10" y1="100" x2="230" y2="100" stroke="{c}" stroke-width="2"/><path d="M150 100 L190 38 L230 100 Z" stroke="{c}" stroke-width="2" fill="none"/><circle cx="190" cy="38" r="4" fill="{c}"/></svg>'
  };

  function familyKeyOf(card) {
    if (card.ownershipTier === 'Universal') return null;
    if (card.ownershipTier === 'Family') return card.ownershipGroup;
    return card.family || null;
  }

  function familyMotifHtml(card) {
    const key = familyKeyOf(card);
    const motif = key && FAMILY_MOTIFS[key];
    // The family colour goes on the ELEMENT rather than being baked into every stroke, so CSS can
    // reach it -- `drop-shadow()` with no colour argument uses `currentColor`, which is what lets
    // the motif carry the same core-plus-bloom treatment as every other lit thing on the board.
    return motif
      ? `<div class="motion-field" style="color:${FAMILY_COLORS[key]}">${motif.split('{c}').join('currentColor')}</div>`
      : '';
  }

  // Dirty Fighting cards are Universal-tier (no Family, so no motion field applies) — per the
  // design doc, they instead get a distinct low-opacity hazard-stripe overlay rather than a new
  // ownership color.
  function dirtyHazardHtml(card) {
    return card.dirty ? '<div class="dirty-hazard"></div>' : '';
  }

  // ---------------- Small shared visual helpers ----------------
  // (moveGlossHtml and rangeBadgeRow went to js/card-tile.js with the tile that prints them; the
  // expanded panels below still call both, through the delegating consts at the top of this file.
  // levelBadgeRow is gone entirely -- three lettered squares were the old way of saying where a
  // card lands, and every surface draws the banded silhouette now.)
  //
  // The dots are a Stamina COST and nothing on the card said so -- a new player reads four circles
  // with no legend. They now carry the word, the same way Power and Guard do, and a title for the
  // pointer. A cost of 0 says "free" rather than showing three empty dots that look like a broken
  // meter.
  function staminaPips(cost) {
    const n = cost || 0;
    const max = Math.max(3, n);
    const label = n ? n + ' Stamina to use this side' : 'No Stamina cost';
    let html = `<div class="stamina-cost${n ? '' : ' is-free'}" title="${label}">`;
    html += '<div class="stamina-pips" aria-hidden="true">';
    for (let i = 0; i < max; i++) html += `<div class="pip ${i < n ? '' : 'free'}"></div>`;
    html += '</div>';
    html += `<span class="stamina-label">${n ? 'COST ' + n : 'FREE'}</span>`;
    return html + '</div>';
  }

  function guardPipsHtml(guard) {
    const n = Math.max(0, guard || 0);
    let out = '';
    for (let i = 0; i < n; i++) out += GUARD_SHIELD;
    return `<span class="guard-pips" aria-hidden="true">${out}</span>`;
  }

  function statFigureHtml(kind, value, label) {
    const glyph = kind === 'power' ? POWER_BURST : GUARD_SHIELD;
    return `<div class="stat-figure ${kind}">${glyph}<b>${value}</b><span>${label}</span></div>`;
  }

  // POWER, PLUS WHAT AN OPENING IS OFFERING — the same grammar the Speed meter already uses for a
  // queued change: the printed number stays the printed number and the modifier rides beside it as
  // its own chip. A player who has learned to read `S2 +1` reads this without being taught.
  //
  // THE PRINTED NUMBER IS NEVER OVERWRITTEN, and that is the whole of why this is safe. The Opening
  // adds to Power at engine.js:1078 and Power then passes through `_attackPowerFloorThisExchange`,
  // `minPower` and `minPowerIf`, any of which can raise it to a floor that swallows the bonus whole
  // — so an adjusted TOTAL printed here would be a number the resolver can contradict, of exactly
  // the kind coverageSentence and the recap's superlatives were both built to avoid. `+2` is an
  // offer the Opening makes. The card's Power is 4 either way.
  //
  // SHOWN ONLY WHEN IT IS THIS CARD'S TO TAKE. A chooseLevel attack with no level picked yet has no
  // single answer — its Opening depends on where the player aims — so nothing is claimed until they
  // aim, and then the chip appears on the level they chose. That is the feature working: the
  // silhouette already marks every Open band, so picking the marked one lights the number. On a
  // fixed-level card the answer is known outright and the chip is simply there.
  function powerFigureHtml(card, chosen, defender) {
    const a = card.attack;
    const base = statFigureHtml('power', a.power, 'POWER');
    if (!defender || !window.ENGINE || !window.ENGINE.openingAt) return base;
    // A fixed-level attack aims where it aims; only one level can be Open to it, and if the card
    // reaches several the first Open one it lists is the one the resolver will find.
    const aimed = chosen || (Array.isArray(a.levels)
      ? a.levels.find(l => window.ENGINE.openingAt(defender, l, card)) : null);
    if (!aimed) return base;
    const open = window.ENGINE.openingAt(defender, aimed, card);
    if (!open) return base;
    const title = open.spendable
      ? `${defender.name} is Open at ${aimed}: +${open.bonus} Power to this Attack, which spends it.`
      : `${defender.name}'s ${aimed} Combination Opening is out of reach — only a Punch can spend it. ` +
        `It stays open.`;
    return `<div class="stat-figure power${open.spendable ? ' has-opening' : ''}" title="${escapeHtml(title)}">` +
      `${POWER_BURST}<b>${a.power}</b>` +
      (open.spendable ? `<em class="power-mod">+${open.bonus}</em>` : '') +
      `<span>POWER</span><span class="visually-hidden">${escapeHtml(title)}</span></div>`;
  }

  // ================= PINNED DECISION DRAWER =================
  // Full reading of ONE card. `role` is 'attack' or 'defense' — which half this card occupies right
  // now (fixed for read-only Select/Telegraph inspection; live-editable in Commit).
  // The rules text is the tallest block in a role panel and the only one that can be deferred
  // without taking information away: the label, the target grid and the Power/Guard value are the
  // numbers you compare cards on, the rules text is the wording behind them. It is also still
  // reachable from the card's own detail view and, once the beat resolves, from the exact log --
  // this is a default, not a hiding place.
  //
  // Collapsed BY AVAILABLE HEIGHT, never by width: the binding constraint has been viewport height
  // at every measurement since D0. The toggle is display:none above the threshold, so on a roomy
  // screen the text simply shows and there is no control to notice.
  // `defaultOpen` is the state before the player has touched this particular disclosure. Commit
  // passes true: that is the screen where the choice is actually being made, and a condition the
  // player never opened is a condition they never knew applied -- "what special rules or use
  // requirements come with this card" is exactly the question Commit exists to answer. The
  // read-only views keep it closed, since nothing is being decided in them and the setup preview
  // has the least room of anywhere.
  //
  // Stored state still wins once it exists, so an explicit collapse survives the re-render that
  // every other Commit choice triggers.

  // THE WORD "CONDITIONS" IS A HEADING FIRST AND A CONTROL SECOND, and it used to be only the
  // second. `.rules-toggle` is `display: none` above the height threshold — the roomy case, where
  // the text simply shows — so on a tall screen the label went with it and the card's own rule
  // rendered as an unheaded sentence floating beside a figure, with nothing saying what it was.
  // The heading and the toggle are the same label in two forms; CSS shows exactly one of them, so
  // this costs no height at any viewport and closes the gap at the one where it was open.
  function panelRulesHtml(key, text, defaultOpen) {
    // Nothing left after the signature means the card face already says everything. Render no
    // section at all rather than a toggle that opens onto blank space.
    if (!text) return '';
    const stored = HOST.storedRulesOpen(key);
    const open = stored === undefined ? !!defaultOpen : !!stored;
    const id = 'rules-' + key;
    return `<div class="panel-rules${open ? ' is-open' : ''}">
      <div class="rules-head">Conditions</div>
      <button type="button" class="rules-toggle" aria-expanded="${open ? 'true' : 'false'}"
              aria-controls="${id}" onclick="CardDetail.toggleRules('${key}')"><span class="rules-toggle-label">Conditions</span></button>
      <div class="effect-text" id="${id}">${escapeHtml(text)}</div>
    </div>`;
  }

  // Toggles in place rather than re-rendering the stage: a re-render would move focus off the
  // button the player just pressed, and Commit re-renders on every other choice as it is.
  function toggleRules(key) {
    const text = document.getElementById('rules-' + key);
    if (!text || !text.parentNode) return;
    // Inverted from what is ON SCREEN, not from the stored flag. With a default-open panel the
    // stored flag is undefined, and `!undefined` is true -- so the first press would "open" an
    // already-open panel and look like a dead button.
    const next = !text.parentNode.classList.contains('is-open');
    // The host decides whether there is anywhere to remember this. The game shell preserves the
    // pre-move rule exactly -- no bout state, no toggle -- and the page-local default always
    // says yes, because a page with no bout still has short viewports.
    if (!HOST.storeRulesOpen(key, next)) return;
    text.parentNode.classList.toggle('is-open', next);
    const btn = text.parentNode.querySelector('.rules-toggle');
    if (btn) btn.setAttribute('aria-expanded', next ? 'true' : 'false');
  }

  // WHAT THE BANDED FIGURE SAYS, IN WORDS. A Defense's coverage is drawn rather than written
  // everywhere on the board, which is right — one diagram beats three lettered squares — but "the
  // top two bands are lit and the bottom one is slate" is obvious once you know the convention and
  // invisible before you do, and the figure is 34px wide in the rack. This is the same fact in a
  // sentence, DERIVED FROM THE SAME zonesFor() THE FIGURE IS DRAWN FROM so the two cannot drift
  // apart: a card whose coverage changes changes both readings or neither.
  //
  // `chosen` is threaded so the sentence tracks a chooseLevels Defense as the pick is made, exactly
  // as the figure does. Returns '' for a Defense that covers nothing, rather than "Blocks ." — and
  // the caller renders no block at all in that case.
  function coverageSentence(card, chosen) {
    const S = window.UISilhouette;
    if (!S || !card || !card.defense) return '';
    const z = S.zonesFor(card, 'defense', chosen || null);
    const active = S.LEVELS.filter(l => z[l] === 'active');
    const legal = S.LEVELS.filter(l => z[l] === 'legal');
    const list = (ls) => ls.length === 1 ? ls[0]
      : ls.slice(0, -1).join(', ') + ' and ' + ls[ls.length - 1];
    if (active.length === S.LEVELS.length) return 'Blocks every level.';
    if (active.length) return 'Blocks ' + list(active) + '.';
    if (legal.length) return 'Blocks ' + legal.join(' or ') + ' — you choose which.';
    return '';
  }

  // Its own class rather than a second `.panel-rules`: the rules block is a DISCLOSURE with stored
  // open/closed state, an aria-controls target and a probe that counts it, and none of that applies
  // to one derived line that is never long enough to fold. Sharing the class would have made every
  // count of "how many rules disclosures are on this card" wrong by one.
  function panelCoverageHtml(card, chosen) {
    const text = coverageSentence(card, chosen);
    if (!text) return '';
    return `<div class="panel-coverage">
      <div class="rules-head">Coverage</div>
      <div class="effect-text">${escapeHtml(text)}</div>
    </div>`;
  }

  // `keyNs` namespaces the rules-disclosure ids. It defaults to `idx`, which is what the Commit
  // pair wants (one key per card slot), but the three READ-ONLY views -- the rail inspector, the
  // setup deck preview and the Select tray -- all call this with idx 0 and so were minting the
  // same `rules-0-a` id as the Commit card on screen beside them. Duplicate ids break
  // getElementById and point every aria-controls at the wrong region.
  // The H/M/L readout on an EXPANDED card. Step 4: the same silhouette the compact card and the
  // Telegraph already use, in place of three lettered squares — so a player reads one diagram
  // everywhere and never has to learn that "H M L" and a lit head band mean the same thing.
  //
  // Bigger here than in the rack (54px against 34px) because an expanded card is where a player
  // reads rather than scans, and because the space is available: steps 5 and 6 took a Tempo radio
  // pair and a segmented Role toggle out of this screen.
  //
  // `chosen` is the player's current pick on a chooseLevel card, so at Commit the diagram tracks
  // the decision as it is made instead of showing the same two dashed bands throughout. Making
  // those bands CLICKABLE is step 7; this is the readout half.
  // `pick` turns the diagram into the CONTROL (step 7). Only the dashed bands become buttons — an
  // active band has nothing left to choose and an off band is not a legal target — so a card with a
  // fixed level renders exactly as before whether or not this is passed.
  //
  // The click is DELEGATED from the wrapper rather than written onto each band, because the bands
  // are emitted by js/ui-silhouette.js, which is a standalone component and has no business knowing
  // that a `UI.` exists. It emits `data-level`; the screen that wants a control says what a level
  // means. A band is a real <button>, so Enter and Space reach this too.
  // `body` is the FIGHTER the figure is drawn on, and it is the only new thing this needs: the
  // Openings marked are that fighter's, because an Opening is a fact about a body and this is the
  // picture of one. It follows `art` exactly — the Attack half is handed the opponent's art and so
  // gets the opponent's Openings, the Defense half the owner's — so no caller has to reason about
  // it twice. Null on the surfaces with no bout behind them (the setup deck browser), which draw
  // exactly as they did before.

  function panelSilhouetteHtml(card, side, art, chosen, pick, reqId, body, gapHint) {
    const S = window.UISilhouette;
    if (!S) return '';
    // THE CARD IS PASSED ONLY ON THE ATTACK SIDE, and the asymmetry is not an oversight. Spendability
    // is a question about the card doing the spending: on the Attack half that card is this one, so
    // a Combination Opening can be resolved to reachable or not. On the Defense half the spender
    // would be whichever card the OPPONENT assigns to Attack — and their roles stay hidden until
    // resolution even though the pair itself is public, so it is genuinely unknown. Passing null
    // there answers the question that can be answered ("you are Open at Mid") and declines the one
    // that cannot; under-warning a defender about their own exposure is the worse of the two errors.
    const openings = (body && window.ENGINE && window.ENGINE.openingsOn)
      ? window.ENGINE.openingsOn(body, side === 'attack' ? card : null) : [];
    return `<div class="panel-sil${pick ? ' is-pickable' : ''}"${pick ? ` onclick="${pick}"` : ''}` +
      `${pick && reqId ? ` id="${reqId}"` : ''}${pick ? ' data-keyboard-alias=".sil-band"' : ''}>` +
      S.render({ art: art, role: side, zones: S.zonesFor(card, side, chosen || null),
                 name: card.name, interactive: !!pick, openings: openings,
                 // ATTACK SIDE ONLY, and for the same reason `card` is: this figure draws the
                 // OPPONENT'S body, so a gap in their guard belongs on it. The Defense figure draws
                 // your own body, where the equivalent question is what their ATTACK can reach --
                 // a different query, and not this one.
                 gapHint: side === 'attack' ? (gapHint || []) : [] }) +
      `</div>`;
  }

  // `art` REPLACED two dead parameters (`fighter`, `opp`) rather than being added as a ninth — both
  // were threaded through every call site and read by nothing. The guard is the same discipline as
  // autoplay's buildChoice: changing what a positional slot means is invisible at a stale call
  // site, and passing a fighter object where a path belongs would yield `src="[object Object]"`,
  // which renders as a broken image rather than as an error.
  function assertArt(art, where) {
    if (art != null && typeof art !== 'string') {
      throw new TypeError(where + ': `art` must be a figure path or null, got ' + typeof art +
        ' — a caller is still passing the removed fighter/opp arguments.');
    }
  }

  // ---- The v4 taxonomy on the card face (Phase 2 of deploy_handoff/speed-edition-plan.md) --------
  //
  // Three facts the Speed Edition adds and a player has to be able to see before committing: the
  // Attack Family, the continuation, and the Defense Family. They go in the PANEL LABEL, which is
  // the only row on this card with spare capacity — it already exists, already carries the attack's
  // category, and adding to it costs no vertical space at all.
  //
  // THAT CONSTRAINT IS THE WHOLE DESIGN. At 1024x576 an open Conditions column renders 17px, which
  // is about one line; at 1366x768 it is fourteen characters wide against a floor of thirteen. Any
  // new ROW would come out of the first budget and any new COLUMN out of the second, and both were
  // already at their limits before this phase started. So: no new row, no new column, and the
  // continuation token — the least load-bearing of the three — is dropped by media query where the
  // label cannot hold it rather than being ellipsised into a lie.
  //
  // WHAT THESE BADGES DO AND DO NOT MEAN, stated because the repo's rule elsewhere is that the
  // display must not lead the engine:
  //   - FAMILY is classification, and it is NOT what the engine matches on. Guard-by-category and
  //     the Muay Thai/Taekwondo passives still read `attack.category`, which agrees with family on
  //     Punch and Kick and disagrees on every Grapple (our Takedown/Throw/Sweep) and on Knee/Elbow
  //     (family null). Both are shown for exactly that reason — see attackPanelHtml.
  //   - CONTINUATION and DEFENSE FAMILY are inert. Nothing in js/engine.js reads either; Phase 4
  //     builds the mechanics. They are printed here as what the card IS, the way the archive prints
  //     them, and the card's Conditions text remains the only statement of what will actually
  //     happen. A player who reads CHAIN and sees no chain has been told the card's family, not
  //     promised an effect — but that gap is real and closes in Phase 4, not here.
  // THE LABEL TABLES USED TO LIVE HERE and are now js/mechanics-dictionary.js. Three copies of the
  // same four sentences existed once this work reached the compact card and the resolution beats,
  // and the brief that commissioned it is explicit that there must be exactly one:
  //
  //     "The UI copy must come from one centralized mechanics dictionary. Do not reproduce the
  //      definitions separately in card, tutorial, resolution, library, and glossary components."
  //
  // What was lost in the move is nothing: the dictionary's `oneLiner` for each key is the same
  // sentence these tables held, corrected where the archive's wording described a rule this engine
  // does not run. What was gained is that library.html and deck-builder.html can show them too --
  // neither can load this file.
  //
  // `extra` classes let CSS reach one token without reaching the others — the continuation is the
  // one that gets dropped at narrow widths, and it can only be dropped if it is addressable.
  //
  // A LABEL, NOT A BUTTON, and the first version of this got it wrong. Making each token tappable
  // put three ~40x15px controls in the panel label row, and tests/browser/phone-check.js measures
  // every control on screen against a 44x44 thumb minimum -- it reported all three at every phone
  // width. Three tiny targets crowded side by side is a worse answer to "reachable in one tap" than
  // one large one, so the tap affordance moved to the consequence sentence below (see
  // panelConsequenceHtml), which is a full-width line of prose and a thumb-sized target by nature.
  // §19's tag, on the panel label beside the family tokens. One entry serves both halves of the
  // mechanic — a loader and a spender are the same coil at two moments — so what distinguishes them
  // is the WORD, not a second glossary row: `RELEASE` on the Attack that spends, `CHAMBER` on the
  // half that loads. Both open the same sheet, which is the point.
  function chamberTokenHtml(half, isAttack) {
    const M = window.MechanicsDictionary;
    if (!M || !half) return '';
    const entry = M.get('chamber', 'Chamber');
    if (!entry) return '';
    if (isAttack && half.release) {
      return ' ' + M.badgeHtml(Object.assign({}, entry, { label: 'RELEASE', shortLabel: 'RLS' }),
        { extraClass: 'v4-token is-release', noIcon: true });
    }
    if (half.chamber) {
      return ' ' + M.badgeHtml(entry, { extraClass: 'v4-token is-chamber', noIcon: true });
    }
    return '';
  }

  function v4TokenHtml(entry, extra) {
    const M = window.MechanicsDictionary;
    if (!entry || !M) return '';
    return ' ' + M.badgeHtml(entry, {
      extraClass: 'v4-token' + (extra ? ' ' + extra : ''),
      noIcon: true
    });
  }

  // THE SENTENCE IS THE CONTROL. It states the rule for anyone who only reads, and opens the card's
  // full mechanics -- every type it carries, with triggers and a worked example -- for anyone who
  // taps. One control, full panel width, no height beyond the sentence it was already going to
  // print, and it is the line a confused player's eye is already on.
  // EVERY TYPE THIS CARD CARRIES, in one sheet: what it is, what its follow-through does, what its
  // Defense does. Reached from the consequence line on either half, which means a player who wants
  // the rule behind the sentence they are reading gets it without leaving the bout -- the brief's
  // "mechanic definitions should be reachable in one tap without navigating away".
  //
  // The same block library.html shows in its card sheet, from the same function, which is the point
  // of putting it in the dictionary rather than here.
  function openCardMechanics(cardId) {
    const M = window.MechanicsDictionary;
    const card = window.CARDS[cardId];
    if (!M || !card || !window.UISheet) return false;
    if (window.MechanicsTelemetry) window.MechanicsTelemetry.note('cardMechanicsOpened', cardId);
    window.UISheet.open({
      title: card.name,
      bodyHtml: M.cardMechanicsHtml(card),
      actions: [{ label: 'Got it', kind: 'primary' }]
    });
    return true;
  }

  // A CAVEAT WITHOUT A SENTENCE STILL RENDERS. This used to bail on `!sentence`, which was right
  // while the only caveat came from defenseCaveat and rode along with a Defense sentence that was
  // always present. The range-fit caveat below does not: attackSentence is composed from the card's
  // continuation data and comes back empty for the cards that have none, and those cards are
  // exactly as capable of being thrown two bands out of position as any other. Bailing here would
  // have made the warning silently absent on a subset of the pool -- the quietest possible failure,
  // and one no assertion about the common case would have caught.
  function panelConsequenceHtml(card, sentence, caveat) {
    if (!sentence && !caveat) return '';
    const canOpen = !!(window.MechanicsDictionary && window.UISheet);
    // THE TEXT IS ITS OWN ELEMENT so the phone band can clamp it to two lines without clipping the
    // button's out-of-flow tap region -- `overflow: hidden` on the button itself hid the ::after
    // that makes it a 44px target, and phone-check reported it at 32px.
    const body = (sentence ? `<span class="panel-consequence-text">${escapeHtml(sentence)}</span>` : '') +
      (caveat ? `<span class="panel-caveat">${escapeHtml(caveat)}</span>` : '');
    if (!canOpen) return `<p class="panel-consequence">${body}</p>`;
    return `<button type="button" class="panel-consequence is-tappable"
      onclick="CardDetail.openCardMechanics('${escapeHtml(card.id)}')"
      aria-label="${escapeHtml((sentence || caveat) + ' Tap to read what this card\'s types do.')}">${body}</button>`;
  }

  // ---------------- Can this attack even be Optimal this exchange? ----------------
  //
  // THE ONE ACCEPTANCE CRITERION OF THE SITEWIDE AUDIT THAT WAS NEVER BUILT: "warn when the selected
  // intent cannot produce an Optimal range for the Attack". It could not be built until
  // ENGINE.clashRangePreview existed, because answering it means running the Intent contest, and
  // that lived inside resolveExchange where no screen could reach it.
  //
  // It is the Commit-time half of the same rule the resolution pill explains after the fact. By
  // then the player has already paid for it. This is the version that can still be acted on: the
  // Intent buttons are three inches away and one of them is usually the answer.
  //
  // WHAT IT IS ALLOWED TO KNOW. The start range and the base three-way cycle, both public. It runs
  // the preview against all three of the OPPONENT's possible Intents and asks whether any reachable
  // Clash is Optimal -- "any", not "all", because the player is choosing under uncertainty and a
  // real chance is not something to warn about. Silence is therefore the common case, which is what
  // keeps this from becoming a line the player learns to stop reading.
  //
  // NAMING THE ALTERNATIVE IS THE TEACHING. "Not Optimal under Evade" is a complaint; "Pressure
  // could reach Pocket" is the rule made usable, and it is the sentence that turns Positioning
  // Intent from a coin flip into a decision.
  const BAND_WORD = { 2: 'two', 3: 'three' };
  function rangeFitCaveat(card, intent, startRange) {
    const E = window.ENGINE;
    if (!E || !E.clashRangePreview || !card || !card.attack || !intent || !startRange) return '';
    const bands = card.attack.optimalRanges;
    // 'All' is never out of position, and a card with no printed bands is not a case to invent a
    // warning for.
    if (!bands || bands === 'All') return '';
    const intents = window.INTENTS || ['Pressure', 'Anchor', 'Evade'];
    const reachedBy = (mine) => intents
      .map(theirs => E.clashRangePreview(startRange, mine, theirs))
      .filter(r => E.rangeEffectiveness(bands, r) === 'Optimal');
    if (reachedBy(intent).length) return '';

    const want = [].concat(bands).join(' or ');
    const alt = intents.filter(o => o !== intent).find(o => reachedBy(o).length);
    if (!alt) {
      // No Intent reaches it, which happens exactly when the card's nearest Optimal band is two
      // bands away — an Intent moves the Clash by one at most, and saying so is the whole reason
      // this sentence teaches rather than merely reports.
      const d = E.bandDistance(bands, startRange);
      return `Cannot be Optimal this exchange: ${want} is ${BAND_WORD[d] || d} bands from ` +
             `${startRange}, and an Intent moves the Clash one band at most.`;
    }
    return `Not Optimal under ${intent} — ${alt} could reach ${reachedBy(alt)[0]}.`;
  }

  // The engine's own finer category, shown beside the ruleset's family only where the two disagree
  // (every Grapple; see attackPanelHtml). It is not a dictionary mechanic -- it is the same word in
  // a different taxonomy -- so it keeps the plain token it always had.
  function categoryTokenHtml(text, title) {
    if (!text) return '';
    return ` <span class="v4-token is-category" title="${escapeHtml(title)}">${escapeHtml(text)}</span>`;
  }

  // `defender` is the fighter this Attack lands on — the same one `art` draws. It is what makes the
  // Opening markers and the Power offer possible, and it is optional: the setup deck browser has no
  // bout behind it and renders exactly as it did before.
  function attackPanelHtml(card, idx, role, draft, art, keyNs, rulesOpen, defender) {
    assertArt(art, 'attackPanelHtml');
    const a = card.attack;
    const chosen = draft ? draft.chosenLevelByIdx[idx] : null;
    // Pickable only where there is genuinely something to pick: this card is the one Attacking,
    // it offers a choice of level, and a draft exists to record it in. The read-only views (the
    // rail inspector, the Select tray, the setup browser) pass no draft and so never get a control.
    const pick = (draft && role === 'attack' && a.chooseLevel)
      ? `UI.silhouettePick(event, ${idx}, 'attack')` : null;
    // The gap marker belongs to the LIVE aiming figure and nowhere else. Scoped on `draft` and the
    // assigned role for the same reason `pick` is: the read-only copies of this panel -- the rail
    // inspector, the Select tray, the setup deck browser -- are looking at a card, not planning an
    // attack with it, and on the rail the card being drawn is the OPPONENT'S, where a note about
    // what the opponent cannot cover would be a sentence about the reader.
    const gapHint = (draft && role === 'attack') ? HOST.uncoveredBands() : [];
    let silHtml, statCol, bodyHtml = '';
    if (a.strikes) {
      bodyHtml = comboTrackHtml(card, idx, draft);
      // MULTI-STRIKE STAYS ON THE BEAT LIST. A five-beat combo can aim each beat separately, and
      // one figure cannot say "beat 1 High, beat 2 Mid" — it would have to show all three bands
      // dashed and then silently apply a click to whichever beat happened to be unset. The
      // silhouette here is a readout of everything the combo can reach; the beats are chosen in
      // the track below it, where each one is named.
      silHtml = panelSilhouetteHtml(card, 'attack', art, null, null, null, defender, gapHint);
      statCol = `<div class="panel-stat-col">${rangeBadgeRow(a.optimalRanges, HOST.boutRange())}${staminaPips(a.cost)}</div>`;
    } else {
      silHtml = panelSilhouetteHtml(card, 'attack', art, chosen, pick, 'req-level-' + idx, defender, gapHint);
      statCol = `<div class="panel-stat-col">
          ${rangeBadgeRow(a.optimalRanges, HOST.boutRange())}
          ${powerFigureHtml(card, chosen, defender)}
          ${staminaPips(a.cost)}
        </div>`;
      bodyHtml = (card.traits || []).indexOf('Throw') >= 0 ? throwModuleHtml() : '';
    }
    // FAMILY AND CATEGORY BOTH, when they differ. `attack.category` is what the engine matches on
    // today and `v4.family` is what the ruleset's conditional checks are written against, and on
    // every Grapple they are different words for the same card — showing only one would either hide
    // the rule the player is reading or hide the rule the engine is running. Where they agree (all
    // 111 Punches and Kicks) only one is printed, because "PUNCH · Punch" is noise.
    // DOES A DECISION LIVE IN THIS PANEL? `pick` is already the answer for a single strike -- it is
    // non-null on exactly the panel that offers a level to aim -- and a combo track is the same
    // thing spread over beats. Both are computed above for the control's own sake; this reads them
    // rather than re-deriving the rule, so a panel that gains or loses its control cannot end up
    // disagreeing with its own disclosure.
    const holdsDecision = !!pick || !!(a.strikes && draft && role === 'attack');
    const openRules = rulesOpen === 'auto' ? holdsDecision : !!rulesOpen;
    const M = window.MechanicsDictionary;
    const typeEntry = M ? M.attackTypeOf(card) : null;
    const contEntry = M ? M.followThroughOf(card) : null;
    // The engine's finer category, printed only where it disagrees with the type the dictionary
    // resolved. On all 111 Punches and Kicks they agree and "PUNCH · Punch" would be noise; on every
    // Grapple they differ, and on Knee/Elbow the dictionary is now reading the category itself, so
    // there is nothing left to disagree with.
    const cat = a.category || null;
    const catSame = typeEntry && cat && typeEntry.key.toUpperCase() === cat.toUpperCase();
    // WHAT THIS CARD WILL DO, IN A SENTENCE, and only on the half that is actually assigned. The
    // brief asks the commit screen to answer "what am I about to cause" before the player confirms,
    // and to collapse the inactive half so it does not compete. The sentence is composed from the
    // card's own structured continuation data, so it cannot drift from what resolution will report.
    const active = role === 'attack';
    const sentence = active && M ? M.attackSentence(card) : '';
    // Scoped on `draft` exactly as `pick` and the gap marker are: the read-only copies of this
    // panel — the rail inspector, the Select tray, the setup deck browser — are looking at a card
    // rather than planning an attack with it, and there is no chosen Intent to warn about.
    const rangeCaveat = (draft && active)
      ? rangeFitCaveat(card, draft.positionIntent, HOST.boutRange()) : '';
    return `<div class="drawer-panel role-attack ${active ? 'active-role' : ''}">
      <div class="panel-label"><b class="panel-role-word">ATTACK</b>${v4TokenHtml(typeEntry, 'is-family')
        }${catSame ? '' : categoryTokenHtml(cat, 'The category this engine matches Guard bonuses and Style passives on.')
        }${v4TokenHtml(contEntry, 'is-continuation')}${chamberTokenHtml(a, true)}</div>
      ${panelConsequenceHtml(card, sentence, rangeCaveat)}
      ${panelMainHtml(silHtml, statCol + bodyHtml +
        panelRulesHtml((keyNs != null ? keyNs : idx) + '-a', conditionsText(card, 'attack'), openRules))}
    </div>`;
  }

  // THE SILHOUETTE AND THE REST OF THE PANEL ARE SIBLINGS, not a stats row with everything stacked
  // beneath it (step 3 of deploy_handoff/commit-card-gap.md, §B and §E).
  //
  // Two things follow from that one change, and they are the two the approved Commit asks for. The
  // figure is no longer bounded by the height of a stats ROW, so it can be the card's centre of
  // gravity instead of a 34px thumbnail beside its numbers; and Conditions become a COLUMN beside
  // the figure rather than a strip competing for whatever height is left under it.
  //
  // The height arithmetic is why this is a gain and not a trade. Stacked, a panel cost
  // label + figure + stats + Conditions, each in series. Side by side it costs
  // label + max(figure, stats + Conditions) — so the figure grows into height the panel was
  // already paying for. That is the whole mechanism; the numbers are in the plan.
  //
  // Uniform markup on every surface, with CSS doing the weighting. The alternative — a second
  // renderer for the Commit card — is the thing step 3's own note warns about for the telegraph:
  // two shapes for one card means a change to either can miss the other.
  function panelMainHtml(silHtml, sideHtml) {
    return `<div class="panel-main">${silHtml}<div class="panel-side">${sideHtml}</div></div>`;
  }

  // `self` is the fighter being defended — the body this figure draws, and whose Openings are the
  // player's own exposure. THE GUARD NUMBER IS NOT TOUCHED, and that is deliberate: an Opening adds
  // to the attacker's Power and never subtracts from Guard, and the two are not interchangeable.
  // `damage = max(0, power - guard)` only holds on a level the Defense COVERS; on one it does not,
  // damage is `power + 1` and Guard is not in the arithmetic at all — so a lowered Guard number
  // would report "nothing changed" in precisely the case that hurts most. Power floors compound it:
  // where one exceeds power+bonus the Opening contributes nothing, and a reduced Guard would show a
  // loss that never happened. The exposure is real and it is shown where it is true — on the band,
  // at its level, on the figure that already speaks levels.
  function defensePanelHtml(card, role, idx, art, chosen, keyNs, rulesOpen, draft, self) {
    assertArt(art, 'defensePanelHtml');
    const d = card.defense;
    const pick = (draft && role === 'defense' && d.chooseLevels)
      ? `UI.silhouettePick(event, ${idx}, 'defense')` : null;
    const statCol = `<div class="panel-stat-col">
          ${statFigureHtml('guard', d.guard, 'GUARD')}
          ${guardPipsHtml(d.guard)}
          ${staminaPips(d.cost)}
        </div>`;
    // Same rule, same source: a Defense panel holds a decision exactly when it is drawing pickable
    // bands. See attackPanelHtml.
    const openRules = rulesOpen === 'auto' ? !!pick : !!rulesOpen;
    const M = window.MechanicsDictionary;
    const defEntry = M ? M.defenseTypeOf(card) : null;
    const active = role === 'defense';
    // Same rule as the Attack panel: the sentence only on the assigned half. `self` is the fighter
    // being defended, which is exactly what the caveat needs -- a full Stamina bar means an Absorb
    // will recover nothing, and that is knowable now rather than after the exchange resolves.
    const sentence = active && M ? M.defenseSentence(card) : '';
    const caveat = active && M ? M.defenseCaveat(card, self) : '';
    return `<div class="drawer-panel role-defense ${active ? 'active-role' : ''}">
      <div class="panel-label"><b class="panel-role-word">DEFENSE</b>${v4TokenHtml(defEntry, 'is-defense-family')}${chamberTokenHtml(d, false)}</div>
      ${panelConsequenceHtml(card, sentence, caveat)}
      ${panelMainHtml(panelSilhouetteHtml(card, 'defense', art, chosen, pick, 'req-defcov-' + idx, self),
        statCol + panelCoverageHtml(card, chosen) +
        panelRulesHtml((keyNs != null ? keyNs : idx) + '-d', conditionsText(card, 'defense'), openRules))}
    </div>`;
  }

  function throwModuleHtml() {
    return `<div class="throw-module">
      <svg width="96" height="56" viewBox="0 0 96 56" fill="none">
        <circle cx="18" cy="14" r="7" stroke="var(--panel-role)" stroke-width="2"/>
        <line x1="18" y1="21" x2="18" y2="38" stroke="var(--panel-role)" stroke-width="2"/>
        <line x1="18" y1="26" x2="30" y2="20" stroke="var(--panel-role)" stroke-width="2"/>
        <line x1="18" y1="38" x2="10" y2="52" stroke="var(--panel-role)" stroke-width="2"/>
        <line x1="18" y1="38" x2="26" y2="52" stroke="var(--panel-role)" stroke-width="2"/>
        <circle cx="76" cy="30" r="7" stroke="var(--role-defense)" stroke-width="2"/>
        <line x1="76" y1="37" x2="66" y2="50" stroke="var(--role-defense)" stroke-width="2"/>
        <path d="M30 20 C50 4, 68 8, 72 24" stroke="var(--accent)" stroke-width="2" fill="none" marker-end="url(#arrow)"/>
        <defs><marker id="arrow" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="var(--accent)"/></marker></defs>
      </svg>
      <div class="notice-row">Projected contact and throw path (illustrative).</div>
    </div>`;
  }

  function comboTrackHtml(card, idx, draft) {
    const d = draft;
    const strikes = card.attack.strikes;
    let track = `<div class="combo-track">`;
    strikes.forEach((s, si) => {
      if (si > 0) track += `<div class="combo-arrow">&#8594;</div>`;
      const level = s.level || (d && d.chosenLevelsByIdx[idx][si]);
      track += `<div class="combo-beat">
        <div class="combo-beat-idx">Beat ${si + 1}</div>
        <div class="combo-beat-level">${level ? level[0] : '?'}</div>
        <div class="combo-beat-power">P${s.power}</div>
      </div>`;
    });
    track += `</div>`;
    if (d) {
      strikes.forEach((s, si) => {
        if (s.chooseLevel) {
          track += `<div class="shelf-item"><span class="timing-badge before-confirm">BEFORE CONFIRM</span>
            <span class="shelf-item-label">Beat ${si + 1} level:</span>
            <div class="option-row">${s.chooseLevel.map(l => `<label class="inline"><input type="radio" id="req-strike-${idx}-${si}" name="strike-${idx}-${si}" value="${l}" ${d.chosenLevelsByIdx[idx][si] === l ? 'checked' : ''} onchange="UI.setCommitStrikeLevel(${idx}, ${si}, '${l}')"> ${l}</label>`).join('')}</div>
          </div>`;
        }
      });
    }
    return track;
  }

  // The opponent's printed card text, on demand.
  //
  // The rail previously showed only name, Tempo and flavour, which left the player planning
  // against cards they could not actually read — the one thing the pinned telegraph exists to
  // support. Printed card text is explicitly public at Telegraph
  // (08_Event_Reveal_Contract.md: "Allowed: ... public printed card text"), so this reveals
  // nothing that was being withheld on purpose; it was simply missing.
  //
  // Click to expand rather than hover: hover has no equivalent on touch and nothing to announce
  // to a screen reader, and the two existing inspect surfaces in this app (the Select pair tray
  // and Telegraph) already work this way.
  // `ownArt` is the figure this card's DEFENSE is drawn on and `foeArt` the one its ATTACK lands
  // on — see figureFor for why they differ. Two callers, and they disagree about both: the rail
  // shows the OPPONENT's pair, so their defense is on them and their attack is on the LOCAL player;
  // the setup browser is looking at a deck outside any bout, so it passes the same figure twice.
  // Both passed in rather than derived here, because this function has no way to know which of
  // those it is serving.
  // `closeIdx` turns the card into the EXPANDED STATE OF A PLANK rather than a standalone panel:
  // it renders the Hide control that collapses it back. Only the Telegraph passes one — the setup
  // deck browser's copy is a pinned pane with its own list to close it from, and a Hide button
  // there would be a second control for something the list already does.
  // `own`/`foe` are the FIGHTERS behind `ownArt`/`foeArt`, and they pair with them exactly: `own`
  // holds the card and is drawn by its Defense half, `foe` is who its Attack lands on. On the
  // Telegraph that makes `foe` the local player, so the Openings marked on the incoming attack are
  // the reader's OWN — which is the right and slightly alarming reading of an opponent's card.
  // Optional: the setup deck browser passes one art twice and no fighters at all.
  function railDetailHtml(cardId, ownArt, foeArt, closeIdx, own, foe) {
    const card = window.CARDS[cardId];
    if (!card) return '';
    if (foeArt === undefined) foeArt = ownArt;
    // `closeIdx` USED TO BE ONLY AN INDEX, and for every caller that predates the player's own
    // ribbons it still is -- a bare number, interpolated bare, meaning the opponent's pair. The
    // rail now holds two pairs during resolution, so the handle has to say WHICH, and a string key
    // ('own:1') interpolated the same way would render as a syntax error inside the attribute.
    // Single quotes because the attribute itself is double-quoted; the keys are built in js/ui.js
    // rather than from card data, and the strip is there so that stays true.
    const closeArg = typeof closeIdx === 'number'
      ? String(closeIdx) : `'${String(closeIdx).replace(/['\\]/g, '')}'`;
    return `
      <div class="drawer rail-detail${closeIdx != null ? ' is-expanded-plank' : ''}"
           style="--frame:${ownershipColor(card)}"${closeIdx != null ? ' data-card-id="' + cardId + '"' : ''}>
        ${familyMotifHtml(card)}${dirtyHazardHtml(card)}
        <div class="drawer-header">
          <div class="drawer-title-row">${speedMeterHtml(card)}${styleSigilHtml(card)}<h3>${escapeHtml(card.name)}</h3>${
            closeIdx != null
              ? `<button type="button" class="ribbon-state configured rail-hide" aria-expanded="true"
                         onclick="UI.setRailInspect(${closeArg})">Hide</button>`
              : ''}</div>
          <div class="drawer-ownership">${ownershipTagsHtml(card)}</div>
        </div>
        ${moveGlossHtml(card, 'drawer-gloss')}
        <div class="drawer-panels">
          ${attackPanelHtml(card, 0, null, null, foeArt, 'rail', false, foe || null)}
          ${defensePanelHtml(card, null, 0, ownArt, null, 'rail', false, null, own || null)}
        </div>
      </div>`;
  }

  // The whole moved set, exported the way card-tile.js exports its own: ui.js re-binds every one
  // of these by name so its call sites read exactly as they did before the move, and a partial
  // export would have forced those call sites to change.
  window.CardDetail = {
    railDetailHtml, attackPanelHtml, defensePanelHtml, panelMainHtml, panelSilhouetteHtml,
    panelRulesHtml, panelCoverageHtml, panelConsequenceHtml, coverageSentence, rangeFitCaveat,
    v4TokenHtml, chamberTokenHtml, categoryTokenHtml, ownershipTagsHtml, familyMotifHtml, dirtyHazardHtml,
    staminaPips, statFigureHtml, powerFigureHtml, guardPipsHtml, throwModuleHtml, comboTrackHtml,
    assertArt, openCardMechanics, toggleRules, bindHost
  };
})();
